/**
 * IXD 편집 세션 store (Zustand, page-scope).
 *
 * 책임: fsType별 cellChanges 보유 + undo stack + raw value 표시 토글.
 * UI 선택 상태(selectedFsType, activeChunkId)는 보유하지 않는다 — 소비처(hook layer)에서 주입.
 *
 * 설계: store는 dumb한 key-value 버킷이며 cellId를 그대로 보존한다.
 * normalize/identity 판정은 store 밖의 편집·저장 boundary에서만 적용한다.
 * 그래서 renderer는 raw column.id로 cellChanges를 그대로 조회할 수 있다.
 */

import { createStore } from "zustand";
import type { FsType } from "@/apis/ixd-editor/types";
import type { CellChangeOrigin, ChangeKey } from "@/utils/ixd-cell-model";

export type FsTypeCellChanges = Record<FsType, Record<ChangeKey, string>>;
export type FsTypeCellChangeOrigins = Record<
  FsType,
  Record<ChangeKey, CellChangeOrigin>
>;

export interface CellChangeEntry {
  fsType: FsType;
  changeKey: ChangeKey;
  newValue: string;
  /** 이 변경이 입력된 실제 보고서 셀 위치들. undo 포커스 목적지에 사용한다. */
  locationUids?: string[];
}

export interface CellChangeReference {
  fsType: FsType;
  changeKey: ChangeKey;
  locationUids?: string[];
}

/**
 * 저장된 셀 참조 + 저장 시점의 값.
 * 저장 후 백그라운드 재조회가 도는 동안에도 편집이 가능하므로, 정리 시점에
 * "그 사이 이 셀이 다시 바뀌지 않았는가"를 값으로 확인해야 한다.
 */
export interface SavedCellChange extends CellChangeReference {
  value: string;
}

/**
 * SavedCellChange 조회 키. 값까지 포함해야 "저장 후 같은 셀 재편집"이 구분된다.
 * 구분자는 NUL — changeKey(identity 키는 JSON 문자열)에 나타나지 않는 문자여야 한다.
 */
const savedCellChangeKey = (
  fsType: FsType,
  changeKey: string,
  value: string,
): string => `${fsType}\u0000${changeKey}\u0000${value}`;

/** 이탈 가드 조회용 인덱스. 재조회 구간엔 저장분이 수백 건일 수 있어 선형 탐색을 피한다. */
export const buildSavedCellChangeIndex = (
  pendingSaved: readonly SavedCellChange[],
): ReadonlySet<string> =>
  new Set(
    pendingSaved.map(({ fsType, changeKey, value }) =>
      savedCellChangeKey(fsType, changeKey, value),
    ),
  );

/**
 * 이탈 가드 판정: 저장된 (fsType, changeKey, value) 그대로 남은 항목만 제외하고,
 * 하나라도 다르면 미저장 변경으로 본다 (clearSavedChanges와 같은 값 대조 기준).
 * 개수 비교는 "저장분 제거 + 신규 입력"이 상쇄돼 가드가 풀리므로 쓰지 않는다.
 */
export const hasUnguardedCellChanges = (
  allCellChanges: FsTypeCellChanges,
  pendingSavedIndex: ReadonlySet<string>,
): boolean =>
  (["consolidated", "separate"] as const).some((fsType) =>
    Object.entries(allCellChanges[fsType]).some(
      ([changeKey, value]) =>
        !pendingSavedIndex.has(savedCellChangeKey(fsType, changeKey, value)),
    ),
  );

interface UndoEntry {
  fsType: FsType;
  changeKey: ChangeKey;
  /** 이전 값. undefined = 편집 전 (변경사항 제거) */
  previousValue: string | undefined;
  /** 이전 변경 출처. 값과 함께 되돌려 셀 모서리 표시도 복원한다. */
  previousOrigin: CellChangeOrigin | undefined;
  locationUids?: string[];
}

export interface UndoResult {
  /** 이번 undo 그룹에서 실제로 되돌린 셀. 같은 셀의 중복 entry는 하나로 합친다. */
  changes: CellChangeReference[];
}

export interface EditSessionState {
  allCellChanges: FsTypeCellChanges;
  allChangeOrigins: FsTypeCellChangeOrigins;
  undoStack: UndoEntry[][];
  showRawValueInEdit: boolean;

  setCellValue: (
    fsType: FsType,
    changeKey: string,
    newValue: string,
    origin?: CellChangeOrigin,
    locationUids?: string[],
  ) => void;
  /**
   * 벌크 갱신을 한 undo 그룹으로 묶어 적용. entry마다 fsType을 명시하므로
   * 단일 fsType 배치도, 양쪽 fsType에 걸친 mixed 배치(주석 등 비교 화면)도
   * 동일한 진입점으로 처리한다.
   */
  setCellValues: (
    entries: CellChangeEntry[],
    origin?: CellChangeOrigin,
  ) => void;
  /**
   * 기존 변경 key를 제거하고 새 변경을 한 undo 그룹으로 적용한다.
   * identity 판정은 hook 경계에서 담당하고 store는 전달받은 raw cellId만 처리한다.
   */
  replaceCellValues: (
    entries: CellChangeEntry[],
    cellsToRemove: CellChangeReference[],
    origin?: CellChangeOrigin,
  ) => void;
  resetCellValue: (fsType: FsType, changeKey: string) => void;
  /** 한 fsType의 변경사항만 비움 (undo stack은 보존) */
  resetFsTypeChanges: (fsType: FsType) => void;
  /** 양 fsType + undo stack 모두 비움 */
  resetAllChanges: () => void;
  /**
   * 저장 후 정리: 저장된 key만 제거하고 나머지(저장 거부분·재조회 중 새 편집분)는 남긴다.
   * 저장 시점 값과 현재 값이 다른 셀은 그 사이 다시 편집된 것이므로 건드리지 않는다.
   * undo stack은 비운다.
   */
  clearSavedChanges: (refs: SavedCellChange[]) => void;

  /** 셀 값 변경 없이 UI 결과만 남는 작업도 독립적인 undo 단계로 기록한다. */
  pushUndoMarker: () => void;
  undo: () => UndoResult | null;
  toggleShowRawValueInEdit: () => void;
}

const EMPTY_CHANGES: FsTypeCellChanges = {
  consolidated: {},
  separate: {},
};
const EMPTY_CHANGE_ORIGINS: FsTypeCellChangeOrigins = {
  consolidated: {},
  separate: {},
};

export const createEditSessionStore = () =>
  createStore<EditSessionState>()((set) => ({
    allCellChanges: { ...EMPTY_CHANGES },
    allChangeOrigins: { ...EMPTY_CHANGE_ORIGINS },
    undoStack: [],
    showRawValueInEdit: false,

    setCellValue: (
      fsType,
      changeKey,
      newValue,
      origin = "manual",
      locationUids,
    ) =>
      set((state) => {
        const prevValue = state.allCellChanges[fsType][changeKey];
        const previousOrigin = state.allChangeOrigins[fsType][changeKey];
        const updated = {
          ...state.allCellChanges[fsType],
          [changeKey]: newValue,
        };
        const updatedOrigins = {
          ...state.allChangeOrigins[fsType],
          [changeKey]: origin,
        };
        return {
          allCellChanges: { ...state.allCellChanges, [fsType]: updated },
          allChangeOrigins: {
            ...state.allChangeOrigins,
            [fsType]: updatedOrigins,
          },
          undoStack: [
            ...state.undoStack,
            [
              {
                fsType,
                changeKey,
                previousValue: prevValue,
                previousOrigin,
                locationUids,
              },
            ],
          ],
        };
      }),

    setCellValues: (entries, origin = "manual") =>
      set((state) => {
        if (entries.length === 0) return state;
        const undoGroup: UndoEntry[] = [];
        const updated: Record<FsType, Record<string, string>> = {
          consolidated: { ...state.allCellChanges.consolidated },
          separate: { ...state.allCellChanges.separate },
        };
        const updatedOrigins: FsTypeCellChangeOrigins = {
          consolidated: { ...state.allChangeOrigins.consolidated },
          separate: { ...state.allChangeOrigins.separate },
        };
        for (const { fsType, changeKey, newValue, locationUids } of entries) {
          undoGroup.push({
            fsType,
            changeKey,
            previousValue: updated[fsType][changeKey],
            previousOrigin: updatedOrigins[fsType][changeKey],
            locationUids,
          });
          updated[fsType][changeKey] = newValue;
          updatedOrigins[fsType][changeKey] = origin;
        }
        return {
          allCellChanges: updated,
          allChangeOrigins: updatedOrigins,
          undoStack: [...state.undoStack, undoGroup],
        };
      }),

    replaceCellValues: (entries, cellsToRemove, origin = "manual") =>
      set((state) => {
        if (entries.length === 0 && cellsToRemove.length === 0) return state;

        const updated: FsTypeCellChanges = {
          consolidated: { ...state.allCellChanges.consolidated },
          separate: { ...state.allCellChanges.separate },
        };
        const updatedOrigins: FsTypeCellChangeOrigins = {
          consolidated: { ...state.allChangeOrigins.consolidated },
          separate: { ...state.allChangeOrigins.separate },
        };
        const undoEntriesByCell = new Map<string, UndoEntry>();
        const rememberPreviousValue = ({
          fsType,
          changeKey,
          locationUids,
        }: CellChangeReference) => {
          const key = `${fsType}\0${changeKey}`;
          const existing = undoEntriesByCell.get(key);
          if (existing) {
            if (locationUids?.length) {
              existing.locationUids = Array.from(
                new Set([...(existing.locationUids ?? []), ...locationUids]),
              );
            }
            return;
          }
          undoEntriesByCell.set(key, {
            fsType,
            changeKey,
            previousValue: state.allCellChanges[fsType][changeKey],
            previousOrigin: state.allChangeOrigins[fsType][changeKey],
            locationUids,
          });
        };

        for (const reference of cellsToRemove) {
          rememberPreviousValue(reference);
          delete updated[reference.fsType][reference.changeKey];
          delete updatedOrigins[reference.fsType][reference.changeKey];
        }
        for (const entry of entries) {
          rememberPreviousValue(entry);
          updated[entry.fsType][entry.changeKey] = entry.newValue;
          updatedOrigins[entry.fsType][entry.changeKey] = origin;
        }

        const undoGroup = Array.from(undoEntriesByCell.values()).filter(
          ({ fsType, changeKey, previousValue, previousOrigin }) => {
            const hasNextValue = changeKey in updated[fsType];
            if (previousValue === undefined) return hasNextValue;
            return (
              !hasNextValue ||
              updated[fsType][changeKey] !== previousValue ||
              updatedOrigins[fsType][changeKey] !== previousOrigin
            );
          },
        );
        if (undoGroup.length === 0) return state;

        return {
          allCellChanges: updated,
          allChangeOrigins: updatedOrigins,
          undoStack: [...state.undoStack, undoGroup],
        };
      }),

    resetCellValue: (fsType, changeKey) =>
      set((state) => {
        if (!(changeKey in state.allCellChanges[fsType])) return state;
        const { [changeKey]: _, ...rest } = state.allCellChanges[fsType];
        const { [changeKey]: _origin, ...restOrigins } =
          state.allChangeOrigins[fsType];
        return {
          allCellChanges: { ...state.allCellChanges, [fsType]: rest },
          allChangeOrigins: {
            ...state.allChangeOrigins,
            [fsType]: restOrigins,
          },
        };
      }),

    resetFsTypeChanges: (fsType) =>
      set((state) => ({
        allCellChanges: { ...state.allCellChanges, [fsType]: {} },
        allChangeOrigins: { ...state.allChangeOrigins, [fsType]: {} },
      })),

    resetAllChanges: () =>
      set({
        allCellChanges: { consolidated: {}, separate: {} },
        allChangeOrigins: { consolidated: {}, separate: {} },
        undoStack: [],
      }),

    clearSavedChanges: (refs) =>
      set((state) => {
        if (refs.length === 0) return state;
        const updated: FsTypeCellChanges = {
          consolidated: { ...state.allCellChanges.consolidated },
          separate: { ...state.allCellChanges.separate },
        };
        const updatedOrigins: FsTypeCellChangeOrigins = {
          consolidated: { ...state.allChangeOrigins.consolidated },
          separate: { ...state.allChangeOrigins.separate },
        };
        for (const { fsType, changeKey, value } of refs) {
          // 저장 후 백그라운드 재조회가 도는 동안 같은 셀을 다시 고쳤다면 새 값이 남아야 한다.
          if (state.allCellChanges[fsType][changeKey] !== value) continue;
          delete updated[fsType][changeKey];
          delete updatedOrigins[fsType][changeKey];
        }
        // ponytail: undo 기록은 저장 이전 상태를 가리켜 저장 후에는 의미가 모호 — 통째로 비운다.
        // 재조회 중에 쌓은 undo도 같이 날아가는 것이 천장. 실사용에서 걸리면
        // "저장 시점 이후 entry만 보존"으로 올린다.
        return {
          allCellChanges: updated,
          allChangeOrigins: updatedOrigins,
          undoStack: [],
        };
      }),

    pushUndoMarker: () =>
      set((state) => ({ undoStack: [...state.undoStack, []] })),

    undo: () => {
      let result: UndoResult | null = null;
      set((state) => {
        if (state.undoStack.length === 0) return state;
        const newStack = state.undoStack.slice(0, -1);
        const group = state.undoStack[state.undoStack.length - 1];
        const uniqueChanges = new Map<string, CellChangeReference>();
        for (const { fsType, changeKey, locationUids } of group) {
          const key = `${fsType}\0${changeKey}`;
          const existing = uniqueChanges.get(key);
          const mergedLocationUids = Array.from(
            new Set([
              ...(existing?.locationUids ?? []),
              ...(locationUids ?? []),
            ]),
          );
          uniqueChanges.set(
            key,
            mergedLocationUids.length > 0
              ? { fsType, changeKey, locationUids: mergedLocationUids }
              : { fsType, changeKey },
          );
        }
        result = { changes: Array.from(uniqueChanges.values()) };
        const newAllChanges = {
          consolidated: { ...state.allCellChanges.consolidated },
          separate: { ...state.allCellChanges.separate },
        };
        const newAllChangeOrigins: FsTypeCellChangeOrigins = {
          consolidated: { ...state.allChangeOrigins.consolidated },
          separate: { ...state.allChangeOrigins.separate },
        };
        // 역순 replay: 같은 cellId가 batch에 두 번 들어와 중간값이 기록된 경우에도
        // 마지막에 가장 오래된(= batch 진입 전) previousValue가 적용되도록 한다.
        for (let i = group.length - 1; i >= 0; i--) {
          const entry = group[i];
          if (entry.previousValue === undefined) {
            delete newAllChanges[entry.fsType][entry.changeKey];
            delete newAllChangeOrigins[entry.fsType][entry.changeKey];
          } else {
            newAllChanges[entry.fsType][entry.changeKey] = entry.previousValue;
            if (entry.previousOrigin === undefined) {
              delete newAllChangeOrigins[entry.fsType][entry.changeKey];
            } else {
              newAllChangeOrigins[entry.fsType][entry.changeKey] =
                entry.previousOrigin;
            }
          }
        }
        return {
          allCellChanges: newAllChanges,
          allChangeOrigins: newAllChangeOrigins,
          undoStack: newStack,
        };
      });
      return result;
    },

    toggleShowRawValueInEdit: () =>
      set((state) => ({ showRawValueInEdit: !state.showRawValueInEdit })),
  }));

export type EditSessionStore = ReturnType<typeof createEditSessionStore>;
